import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { ArrowRight, Map } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function ActionPlanStep({ sessionNumber, onDone }) {
  const [d, setD] = useState({ goal: "", tasks: "", deadline: "", resources: "", motivation: "", commitment: 8 });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!d.goal.trim() || !d.tasks.trim()) {
      toast.error("Define al menos tu objetivo principal y las tareas.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/progress", { session_number: sessionNumber, step: "action-plan", completed: true, payload: d });
      toast.success("Plan de acción guardado");
      onDone(d);
    } catch {
      toast.error("No pudimos guardar tu plan.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <p className="text-[var(--ha-muted)] leading-relaxed max-w-2xl">Diseña un plan de acción que integre todo lo aprendido en MINDHOME HEALTH APP. Sé específico/a y ambicioso/a pero realista.</p>
      <Card className="mt-6 rounded-2xl border-[var(--ha-border)] bg-white">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <Map className="w-4 h-4 text-[var(--ha-primary)]" strokeWidth={1.75} />
            <p className="text-xs uppercase tracking-[0.14em] text-[var(--ha-muted)] font-bold">Plan de acción</p>
          </div>
          <div>
            <label className="text-sm font-semibold text-[var(--ha-text)]">Objetivo SMART principal</label>
            <Input data-testid="ap-goal" value={d.goal} onChange={(e) => setD({ ...d, goal: e.target.value })} placeholder="Ej: Practicar respiración consciente 10 min diarios durante 3 meses" className="mt-2 rounded-xl border-[var(--ha-border)]" />
          </div>
          <div>
            <label className="text-sm font-semibold text-[var(--ha-text)]">Tareas específicas (una por línea)</label>
            <Textarea data-testid="ap-tasks" value={d.tasks} onChange={(e) => setD({ ...d, tasks: e.target.value })} placeholder="1. Programar recordatorio diario\n2. Preparar espacio\n3. Registrar experiencia..." className="mt-2 rounded-xl border-[var(--ha-border)]" rows={5} />
          </div>
          <div>
            <label className="text-sm font-semibold text-[var(--ha-text)]">Fecha límite</label>
            <Input data-testid="ap-deadline" value={d.deadline} onChange={(e) => setD({ ...d, deadline: e.target.value })} placeholder="Ej: 30 de junio" className="mt-2 rounded-xl border-[var(--ha-border)]" />
          </div>
          <div>
            <label className="text-sm font-semibold text-[var(--ha-text)]">Recursos necesarios</label>
            <Textarea data-testid="ap-resources" value={d.resources} onChange={(e) => setD({ ...d, resources: e.target.value })} placeholder="Tiempo, materiales, apoyo social..." className="mt-2 rounded-xl border-[var(--ha-border)]" />
          </div>
          <div>
            <label className="text-sm font-semibold text-[var(--ha-text)]">Estrategias de motivación</label>
            <Textarea data-testid="ap-motivation" value={d.motivation} onChange={(e) => setD({ ...d, motivation: e.target.value })} placeholder="Autoinstrucciones, recompensas, apoyo, etc." className="mt-2 rounded-xl border-[var(--ha-border)]" />
          </div>
          <div>
            <label className="text-sm font-semibold text-[var(--ha-text)]">Nivel de compromiso (1-10)</label>
            <div className="mt-3 flex items-center gap-4">
              <Slider data-testid="ap-commitment" value={[d.commitment]} onValueChange={([v]) => setD({ ...d, commitment: v })} min={1} max={10} step={1} className="flex-1" />
              <span className="text-sm font-bold text-[var(--ha-primary)] w-8 text-right">{d.commitment}</span>
            </div>
          </div>
        </CardContent>
      </Card>
      <div className="mt-8 flex justify-end">
        <Button data-testid="ap-continue" onClick={save} disabled={saving} className="rounded-full bg-[var(--ha-primary)] hover:bg-[var(--ha-primary-hover)] text-white h-11 px-6">
          Guardar plan <ArrowRight className="w-4 h-4 ml-1.5" strokeWidth={1.75} />
        </Button>
      </div>
    </div>
  );
}
